import React, { useCallback, useEffect, useMemo } from 'react';
import { Column, ColumnInstance, Row, usePagination, useSortBy, useTable } from 'react-table';
import styles from './Table.module.css';
import LoadingSpinner from '../../../../SharedComponents/LoadingSpinner/LoadingSpinner';
import cn from 'classnames';
import LeftArrowIcon from '../../../../SharedComponents/svg/Icons/LeftArrowIcon';
import RightArrowIcon from '../../../../SharedComponents/svg/Icons/RightArrowIcon';
import {
    OrderByParam,
    useOrderByToReactTableSortBy,
    useReactTableSortToOrderBy,
} from '../../../../Hooks/useReactTableSortToOrderBy';
import { ReactComponent as OrderByDefault } from '../../../../SharedComponents/svg/OrderByDefault.svg';
import { ReactComponent as OrderByDesc } from '../../../../SharedComponents/svg/OrderByDesc.svg';
import { ReactComponent as OrderByAsc } from '../../../../SharedComponents/svg/OrderByAsc.svg';

export type RenderFooterProps = {
    canPreviousPage: boolean;
    canNextPage: boolean;
    goNext: () => void;
    goPrev: () => void;
    setPageSize: (pageSize: number) => void;
    page: number;
    pageCount: number;
};

export type PaginationState<T extends object> = {
    pageRows: Row<T>[];
};

export type TableProps<T extends object> = {
    data: T[];
    columns: Column<T>[];
    loading?: boolean;
    noDataLabel?: string;
    onRowClick?: (item: T) => void;
    pageSize?: number;
    manualPagination?: boolean;
    count?: number;
    onPageIndexChange?: (pageIndex: number) => void;
    manualSortBy?: boolean;
    autoResetPage?: boolean;
    orderBy?: OrderByParam<T>;
    onOrderByChange?: (item: OrderByParam<T>) => void;
    disableSortBy?: boolean;
    pageIndex?: number;
    tableClassName?: string;
    theadClassName?: string;
    tbodyClassName?: string;
    tfootClassName?: string;
    renderFooter?: (params: RenderFooterProps) => React.ReactNode;
    onPaginationChange?: (state: PaginationState<T>) => void;
};

const Table = <T extends object>({
    data,
    columns,
    noDataLabel,
    loading,
    onRowClick,
    count,
    pageSize = 10,
    manualPagination,
    autoResetPage = true,
    onPageIndexChange,
    manualSortBy,
    onOrderByChange,
    orderBy = {},
    disableSortBy,
    pageIndex,
    theadClassName,
    tbodyClassName,
    tfootClassName,
    renderFooter,
    tableClassName,
    onPaginationChange,
}: TableProps<T>) => {
    const sortBy = useOrderByToReactTableSortBy(orderBy);
    const {
        getTableProps,
        headers,
        rows,
        page,
        prepareRow,
        state: { pageIndex: statePageIndex, pageSize: statePageSize, sortBy: stateSortBy },
        nextPage,
        previousPage,
        canNextPage,
        canPreviousPage,
        setPageSize,
        pageCount,
    } = useTable(
        {
            data,
            columns,
            manualPagination,
            autoResetPage,
            pageCount: manualPagination && typeof count === 'number' ? Math.ceil(count / pageSize) : -1,
            manualSortBy,
            disableSortBy: !data.length || disableSortBy,
            disableMultiSort: true,
            initialState: {
                pageSize,
                sortBy,
            },
            useControlledState: (state) =>
                useMemo(() => {
                    if (manualPagination && typeof pageIndex === 'number') {
                        state.pageIndex = pageIndex;
                    }

                    if (manualSortBy) {
                        state.sortBy = sortBy;
                    }

                    return state;
                    // eslint-disable-next-line
                }, [state, manualPagination, pageIndex, sortBy]),
        },
        useSortBy,
        usePagination
    );

    const orderByParam = useReactTableSortToOrderBy(stateSortBy);
    const rowsFrom = useMemo(
        () => (rows.length > 0 ? statePageIndex * statePageSize + 1 : 0),
        [statePageIndex, statePageSize, rows]
    );
    const rowsTo = useMemo(
        () => statePageIndex * statePageSize + page.length,
        [statePageIndex, statePageSize, page.length]
    );

    const onNextPageClicked = useCallback(() => {
        if (!canNextPage || loading) {
            return;
        }

        if (manualPagination && typeof onPageIndexChange === 'function' && typeof pageIndex === 'number') {
            onPageIndexChange(pageIndex + 1);
            return;
        }

        nextPage();
    }, [canNextPage, loading, manualPagination, nextPage, onPageIndexChange, pageIndex]);

    const onPrevPageClicked = useCallback(() => {
        if (!canPreviousPage || loading) {
            return;
        }

        if (manualPagination && typeof onPageIndexChange === 'function' && typeof pageIndex === 'number') {
            onPageIndexChange(pageIndex - 1);
            return;
        }

        previousPage();
    }, [canPreviousPage, loading, manualPagination, onPageIndexChange, pageIndex, previousPage]);

    const onToggleSortBy = useCallback(
        (column: ColumnInstance<T>) => {
            if (typeof onOrderByChange !== 'function') {
                return;
            }

            const orderBy: OrderByParam<T> = {};
            if (typeof column.isSortedDesc === 'undefined') {
                orderBy['order_by_asc'] = column.id as keyof T;
            }

            if (typeof column.isSortedDesc === 'boolean' && !column.isSortedDesc) {
                orderBy['order_by_desc'] = column.id as keyof T;
            }

            onOrderByChange(orderBy);
        },
        [onOrderByChange]
    );

    useEffect(() => {
        if (typeof onOrderByChange === 'function' && !manualSortBy) {
            onOrderByChange(orderByParam);
        }
    }, [manualSortBy, onOrderByChange, orderByParam]);

    useEffect(() => {
        if (typeof onPageIndexChange === 'function' && !manualPagination) {
            onPageIndexChange(statePageIndex);
        }
    }, [manualPagination, onPageIndexChange, statePageIndex]);

    useEffect(() => {
        if (typeof onPaginationChange === 'function') {
            onPaginationChange({ pageRows: page });
        }
    }, [onPaginationChange, page]);

    return (
        <div className={styles.tableContainer}>
            <table className={cn(styles.table, tableClassName)} {...getTableProps()}>
                <thead className={theadClassName}>
                    <tr className={styles.headerRow}>
                        {headers.map((column) => (
                            <th
                                className={styles.headerCell}
                                {...column.getHeaderProps(
                                    column.getSortByToggleProps(
                                        // override to make controlled
                                        manualSortBy
                                            ? {
                                                  onClick: () => {
                                                      if (!column.disableSortBy) {
                                                          onToggleSortBy(column);
                                                      }
                                                  },
                                              }
                                            : undefined
                                    )
                                )}
                            >
                                {column.render('Header')}
                                {column.canSort && (
                                    <span className={styles.sortingIcon}>
                                        {typeof column.isSortedDesc === 'undefined' ? (
                                            <OrderByDefault />
                                        ) : column.isSortedDesc ? (
                                            <OrderByDesc />
                                        ) : (
                                            <OrderByAsc />
                                        )}
                                    </span>
                                )}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody className={tbodyClassName}>
                    {!rows.length && (
                        <tr>
                            <td colSpan={columns.length}>
                                {loading ? <LoadingSpinner /> : <div className={styles.noDataLabel}>{noDataLabel}</div>}
                            </td>
                        </tr>
                    )}
                    {page.map((row) => {
                        prepareRow(row);
                        return (
                            <tr
                                className={cn(styles.tableRow, {
                                    [styles.clickable]: typeof onRowClick === 'function',
                                    [styles.loading]: loading,
                                })}
                                {...row.getRowProps()}
                                onClick={() => typeof onRowClick === 'function' && !loading && onRowClick(row.original)}
                            >
                                {row.cells.map((cell) => (
                                    <td className={styles.bodyCell} {...cell.getCellProps()}>
                                        {cell.render('Cell')}
                                    </td>
                                ))}
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot>
                    <tr>
                        <td colSpan={columns.length}>
                            {typeof renderFooter === 'function' &&
                                renderFooter({
                                    canNextPage,
                                    canPreviousPage,
                                    setPageSize,
                                    goNext: onNextPageClicked,
                                    goPrev: onPrevPageClicked,
                                    page: statePageIndex + 1,
                                    pageCount,
                                })}
                            {typeof renderFooter !== 'function' && (
                                <div className={cn(styles.tableFoot, tfootClassName)}>
                                    <span>
                                        {rowsFrom}-{rowsTo} of {count ?? data.length}
                                    </span>

                                    <LeftArrowIcon
                                        ariaRole="button"
                                        ariaLabel="Go to previous page"
                                        className={cn(styles.paginationIcon, {
                                            [styles.paginationIconDisabled]: !canPreviousPage || loading,
                                        })}
                                        width={12}
                                        height={12}
                                        onClick={onPrevPageClicked}
                                    />

                                    <RightArrowIcon
                                        ariaRole="button"
                                        ariaLabel="Go to next page"
                                        className={cn(styles.paginationIcon, {
                                            [styles.paginationIconDisabled]: !canNextPage || loading,
                                        })}
                                        height={12}
                                        width={12}
                                        onClick={onNextPageClicked}
                                    />
                                </div>
                            )}
                        </td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );
};

export default Table;
